import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ContractV2T, MeterV2T, SiteV2T, CustomerV2T, InstantTelemetryV2 } from "../../types";

// Types
interface MeterDetails {
  status: string;
  name: string;
  serialNumber: string;
  meterCommodityType: string;
  installationDate: string;
}

interface MeterItem {
  meterId: string;
  serialNumber: string;
  meterType: string;
  meterStatus: string;
  lastReadingDate?: string;
  lastReadValue?: string;
  id: string;
}

interface MeterPayload {
  name?: string;
  additionalInfo: {
    SerialNumber?: string;
    status?: string;
    InstallationDate?: string;
    meterCommodityType?: string;
    MeterId?: string;
  };
  status?: string;
  id?: {
    id: string;
  };
}

interface MeterState {
  hasMeterSearched: boolean;
  isMeterLoading: boolean;
  meterId: string;
  meterDetails: MeterDetails;
  totalMeters: number;
  allMeters: MeterItem[];
  meterV2Details: MeterV2T | null;
  siteV2Details: SiteV2T | null;
  contractV2Details: ContractV2T | null;
  instantTelemetryV2: InstantTelemetryV2 | null;
}

// initial states
const initialState: MeterState = {
  hasMeterSearched: false,
  isMeterLoading: false,
  meterId: "",
  meterDetails: {
    status: "",
    name: "",
    serialNumber: "",
    meterCommodityType: "",
    installationDate: "",
  },
  totalMeters: 4,
  allMeters: [],
  meterV2Details: null,
  siteV2Details: null,
  contractV2Details: null,
  instantTelemetryV2: null,
};

const reducers = {
  setMeterDetails: (state: MeterState, action: PayloadAction<MeterPayload>) => {
    const { SerialNumber, status, InstallationDate, meterCommodityType } =
      action.payload.additionalInfo;
    state.meterDetails.name = action.payload.name || "John Smith";
    state.meterDetails.serialNumber = SerialNumber || "12345678";
    state.meterDetails.status = status || "active";
    state.meterDetails.meterCommodityType = meterCommodityType || "Residential";
    state.meterDetails.installationDate = InstallationDate || "01 Jan 24";
  },
  setTotalMeters: (state: MeterState, action: PayloadAction<number>) => {
    state.totalMeters = action.payload;
  },
  setMeterId: (state: MeterState, action: PayloadAction<string>) => {
    state.meterId = action.payload;
  },
  setHasMeterSearched: (state: MeterState, action: PayloadAction<boolean>) => {
    state.hasMeterSearched = action.payload;
  },
  setIsMeterLoading: (state: MeterState, action: PayloadAction<boolean>) => {
    state.isMeterLoading = action.payload;
  },
  setAllMeters: (state: MeterState, action: PayloadAction<MeterPayload[]>) => {
    if (action.payload.length > 0) {
      const allMeters: MeterItem[] = action.payload.map((payload: any) => {
        const additionalInfo = payload?.additionalInfo || {};
        const { MeterId, SerialNumber, meterCommodityType, status: addStatus } = additionalInfo;

        return {
          meterId: payload.id || MeterId || "-" || state.allMeters[0]?.meterId,
          serialNumber:
            payload.meterNumber ||
            SerialNumber ||
            payload?.name ||
            "-" ||
            state.allMeters[0]?.serialNumber,
          meterType: payload.meterType || meterCommodityType || "-" || state.allMeters[0]?.meterType,
          meterStatus:
            payload.connectionStatus ||
            payload.loadStatus ||
            addStatus ||
            payload?.status ||
            "-" ||
            state.allMeters[0]?.meterStatus,
          id: (typeof payload?.id === 'object' ? payload?.id?.id : payload?.id) || "",
        };
      });

      state.allMeters = allMeters;
    } else {
      state.allMeters = [];
    }
  },
  setMeterV2Details: (state: MeterState, action: PayloadAction<MeterV2T | null>) => {
    state.meterV2Details = action.payload;
  },
  setSiteV2Details: (state: MeterState, action: PayloadAction<SiteV2T | null>) => {
    state.siteV2Details = action.payload;
  },
  setContractV2Details: (state: MeterState, action: PayloadAction<ContractV2T | null>) => {
    state.contractV2Details = action.payload;
  },
  setInstantTelemetryV2: (state: MeterState, action: PayloadAction<InstantTelemetryV2 | null>) => {
    state.instantTelemetryV2 = action.payload;
  },
};

// creating meter slice
const MeterSlice = createSlice({
  name: "meter",
  initialState,
  reducers,
});

// exporting actions
export const {
  setMeterDetails,
  setAllMeters,
  setTotalMeters,
  setMeterId,
  setHasMeterSearched,
  setIsMeterLoading,
  setMeterV2Details,
  setSiteV2Details,
  setContractV2Details,
  setInstantTelemetryV2,
} = MeterSlice.actions;

export default MeterSlice.reducer;
